import React, { useState } from "react";
import StatusChip from "./StatusChip";
import { api } from "../lib/api";
import { CheckCircle2, AlertTriangle, ExternalLink, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";

function MonoRow({ label, value, testId }) {
    return (
        <div className="grid grid-cols-12 gap-4 px-5 md:px-6 py-3 border-b border-parchment-rule last:border-b-0">
            <div className="col-span-12 md:col-span-3 label-eyebrow self-center">{label}</div>
            <div className="col-span-12 md:col-span-9 data-cell self-center" data-testid={testId}>
                {value ?? <span className="text-ink-muted">—</span>}
            </div>
        </div>
    );
}

/**
 * Audit envelope-style receipt card. Public-safe fields only.
 */
export default function ReceiptEnvelope({ receipt, onVerified, showVerify = true, showPublicLink = true }) {
    const [busy, setBusy] = useState(false);
    const [localStatus, setLocalStatus] = useState(receipt?.verification_status);

    if (!receipt) return null;

    const verified = localStatus === "verified" || receipt.verification_status === "verified";

    const handleVerify = async () => {
        if (!receipt.receipt_hash) return;
        setBusy(true);
        try {
            const res = await api.verifyReceipt(receipt.receipt_hash);
            setLocalStatus(res.verification_status);
            if (onVerified) onVerified(res);
        } catch (e) {
            setLocalStatus("verification_failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className={`relative animate-stamp-in border border-parchment-rule ${verified ? "receipt-verified" : "receipt-paper"}`}
            data-testid="receipt-envelope"
        >
            <div className="px-5 md:px-6 py-4 border-b border-parchment-rule flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-baseline gap-3">
                    <span className="label-eyebrow">KATLAS Receipt</span>
                    <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-ink-muted">
                        {receipt.receipt_type || "governed_action"}
                    </span>
                </div>
                <StatusChip
                    status={localStatus || receipt.verification_status}
                    testId="receipt-verification-status"
                />
            </div>

            <MonoRow label="Receipt Hash" value={receipt.receipt_hash} testId="receipt-hash" />
            <MonoRow label="Signer Reference" value={receipt.signer_reference} testId="receipt-signer" />
            <MonoRow label="Node Timestamp" value={receipt.node_timestamp} testId="receipt-node-timestamp" />
            <MonoRow label="Event ID" value={receipt.event_id} testId="receipt-event-id" />
            <MonoRow label="Policy Reference" value={receipt.policy_id} testId="receipt-policy-id" />
            <MonoRow label="Actor → Counterparty" value={`${receipt.actor_role || "—"}  →  ${receipt.counterparty_role || "—"}`} testId="receipt-actors" />
            <MonoRow label="Outcome" value={receipt.outcome} testId="receipt-outcome" />
            <MonoRow
                label="Shared Evidence"
                value={
                    receipt.shared_evidence_refs?.length
                        ? receipt.shared_evidence_refs.join("  ·  ")
                        : "—"
                }
                testId="receipt-shared-refs"
            />
            <MonoRow
                label="Withheld Evidence"
                value={`${receipt.withheld_evidence_count ?? 0} item(s) — kept at source`}
                testId="receipt-withheld-count"
            />
            <MonoRow
                label="Artifact Hash"
                value={receipt.artifact_hash}
                testId="receipt-artifact-hash"
            />

            {receipt.car_summary && (
                <div className="border-t border-parchment-rule">
                    <div className="px-5 md:px-6 py-3 label-eyebrow">CAR Summary on Receipt</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 border-t border-parchment-rule">
                        <div className="p-5 border-r last:border-r-0 border-parchment-rule">
                            <div className="label-eyebrow mb-2">Custody</div>
                            <p className="text-sm">{receipt.car_summary.custody}</p>
                        </div>
                        <div className="p-5 border-r last:border-r-0 border-parchment-rule">
                            <div className="label-eyebrow mb-2">Authority</div>
                            <p className="text-sm">{receipt.car_summary.authority}</p>
                        </div>
                        <div className="p-5">
                            <div className="label-eyebrow mb-2">Receipts</div>
                            <p className="text-sm">{receipt.car_summary.receipts}</p>
                        </div>
                    </div>
                </div>
            )}

            <div className="px-5 md:px-6 py-4 border-t border-parchment-rule flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-xs text-ink-muted font-mono uppercase tracking-[0.18em]">
                    {verified ? (
                        <CheckCircle2 size={14} strokeWidth={1.5} className="text-forest" />
                    ) : (
                        <AlertTriangle size={14} strokeWidth={1.5} className="text-ochre" />
                    )}
                    {verified
                        ? "Verified against KATLAS node"
                        : "Public-safe metadata only · raw envelope kept server-side"}
                </div>
                <div className="flex items-center gap-2">
                    {showVerify && (
                        <button
                            className="btn-ghost"
                            onClick={handleVerify}
                            disabled={busy}
                            data-testid="verify-receipt-btn"
                        >
                            <RefreshCw size={14} strokeWidth={1.5} className={busy ? "animate-spin" : ""} />
                            {busy ? "Verifying…" : "Verify with KATLAS"}
                        </button>
                    )}
                    {showPublicLink && receipt.receipt_hash && (
                        <Link
                            to={`/r/${encodeURIComponent(receipt.receipt_hash)}`}
                            className="btn-ghost"
                            data-testid="open-public-receipt-link"
                        >
                            <ExternalLink size={14} strokeWidth={1.5} />
                            Public receipt
                        </Link>
                    )}
                </div>
            </div>
        </div>
    );
}
